import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Clock, MapPin, Users, Trophy, Settings, Image, Home, Plane } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { TeamLogoUpload } from '@/components/teams/TeamLogoUpload';
import { TeamLogo } from './TeamLogo';
import { TeamGameCard } from './TeamGameCard';
import { GameCountdownTimer } from './GameCountdownTimer';
import { EnhancedVenueSelector } from '@/components/venue/EnhancedVenueSelector';
import { useGameManagement } from '@/hooks/useGameManagement';
import { useTeams } from '@/hooks/useTeams';
import { useUserRole } from '@/hooks/useUserRole';
import { sportsService, SportConfiguration } from '@/services/sportsService';
import { SportVariantSelector } from './SportVariantSelector';
import { DivisionSelector } from './DivisionSelector';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import type { CreateGameData } from '@/types/gameSystem';
import { COMMON_TIMEZONES, getUserTimezone } from '@/utils/timezone';

interface EnhancedGameCreationModalProps {
  userId: string;
  trigger?: React.ReactNode;
  onGameCreated?: () => void;
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}

const normalizeSportName = (value?: string | null) =>
  value?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';

export const EnhancedGameCreationModal: React.FC<EnhancedGameCreationModalProps> = ({
  userId,
  trigger,
  onGameCreated,
  externalOpen,
  onExternalOpenChange
}) => {
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (val: boolean) => {
    if (onExternalOpenChange) onExternalOpenChange(val);
    setInternalOpen(val);
  };
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateGameData>({
    game_name: '',
    game_date: '',
    game_time: '',
    game_timezone: getUserTimezone(),
    venue_id: undefined,
    venue_name: '',
    venue_address: '',
    field_location: '',
    game_description: '',
    sport_id: undefined,
    age_group: undefined,
    home_team_id: undefined,
    away_team_id: undefined,
    home_team_label: 'Home',
    away_team_label: 'Away',
    home_team_is_tbd: false,
    away_team_is_tbd: false,
    minimum_players_required: 5
  });

  const [selectedDivision, setSelectedDivision] = useState('');

  const [selectedSportVariant, setSelectedSportVariant] = useState<SportConfiguration | null>(null);

  const [teamLogos, setTeamLogos] = useState<{
    home?: string;
    away?: string;
  }>({});

  const { createGame, loading } = useGameManagement(userId);
  const { teams } = useTeams();
  const { permissions } = useUserRole();
  const [sports, setSports] = useState<Array<{id: number, sport_name: string}>>([]);
  const [sportsLoading, setSportsLoading] = useState(true);

  // Check if user is venue
  const isVenue = permissions.isVenue;

  const selectedSportName = useMemo(
    () => sports.find((sport) => sport.id === formData.sport_id)?.sport_name || '',
    [sports, formData.sport_id]
  );

  const matchingTeams = useMemo(() => {
    const normalizedSelectedSport = normalizeSportName(selectedSportName);

    if (!normalizedSelectedSport) return [];

    return teams.filter(
      (team) => normalizeSportName(team.sport_type) === normalizedSelectedSport
    );
  }, [selectedSportName, teams]);

  const matchingUserTeams = useMemo(
    () => matchingTeams.filter((team) => team.created_by === userId),
    [matchingTeams, userId]
  );

  const homeTeamOptions = useMemo(() => {
    const baseTeams = isVenue
      ? matchingTeams
      : formData.created_by_team_side === 'home'
        ? matchingUserTeams
        : matchingTeams;

    return baseTeams.filter((team) => team.team_id !== formData.away_team_id);
  }, [isVenue, matchingTeams, matchingUserTeams, formData.created_by_team_side, formData.away_team_id]);

  const awayTeamOptions = useMemo(() => {
    const baseTeams = isVenue
      ? matchingTeams
      : formData.created_by_team_side === 'away'
        ? matchingUserTeams
        : matchingTeams;

    return baseTeams.filter((team) => team.team_id !== formData.home_team_id);
  }, [isVenue, matchingTeams, matchingUserTeams, formData.created_by_team_side, formData.home_team_id]);

  // Load sports from database
  useEffect(() => {
    const loadSports = async () => {
      try {
        setSportsLoading(true);
        const availableSports = await sportsService.getAvailableSports();
        setSports(availableSports);
      } catch (error) {
        console.error('Error loading sports:', error);
        toast.error('Failed to load sports');
      } finally {
        setSportsLoading(false);
      }
    };

    loadSports();
  }, []);

  useEffect(() => {
    const validTeamIds = new Set(matchingTeams.map((team) => team.team_id));

    setFormData((prev) => {
      const nextHomeTeamId = prev.home_team_id && validTeamIds.has(prev.home_team_id)
        ? prev.home_team_id
        : undefined;
      const nextAwayTeamId = prev.away_team_id && validTeamIds.has(prev.away_team_id)
        ? prev.away_team_id
        : undefined;

      if (nextHomeTeamId === prev.home_team_id && nextAwayTeamId === prev.away_team_id) {
        return prev;
      }

      return {
        ...prev,
        home_team_id: nextHomeTeamId,
        away_team_id: nextAwayTeamId,
      };
    });
  }, [matchingTeams]);

  const handleInputChange = (field: keyof CreateGameData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      // Use local date to prevent timezone offset issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const localDateString = `${year}-${month}-${day}`;
      
      setFormData(prev => ({
        ...prev,
        game_date: localDateString
      }));
    }
  };

  const handleLogoUpload = (side: 'home' | 'away', logoUrl: string) => {
    setTeamLogos(prev => ({
      ...prev,
      [side]: logoUrl
    }));
  };

  const validateStep = (step: number) => {
    switch (step) {
      case 1:
        return formData.game_name && formData.game_date && formData.game_time && formData.sport_id && formData.age_group && selectedDivision;
      case 2:
        return formData.venue_name && formData.venue_address;
      case 3:
        // For venues: both teams can be selected or TBD
        // For coaches/team-owners: must have selected a side and at least one team
        if (isVenue) {
          return formData.home_team_id || formData.away_team_id || formData.home_team_is_tbd || formData.away_team_is_tbd;
        }
        return formData.created_by_team_side && (formData.home_team_id || formData.away_team_id || formData.home_team_is_tbd || formData.away_team_is_tbd);
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      // Enhanced authentication check
      if (!userId) {
        toast.error('Authentication required. Please refresh the page and log in again.');
        return;
      }

      // Authentication is handled by the useGameManagement hook based on user type
      console.log('🎮 Modal handleSubmit - Authentication will be verified by service based on user type');

      // Validation
      if (!formData.game_name?.trim()) {
        toast.error('Game name is required');
        return;
      }
      
      if (!formData.game_date) {
        toast.error('Game date is required');
        return;
      }
      
      if (!formData.game_time) {
        toast.error('Game time is required');
        return;
      }
      
      if (!formData.sport_id) {
        toast.error('Sport selection is required');
        return;
      }

      if (!formData.age_group) {
        toast.error('Age group selection is required');
        return;
      }

      // Team validation - at least one team must be assigned
      if (!formData.home_team_id && !formData.away_team_id && !formData.home_team_is_tbd && !formData.away_team_is_tbd) {
        toast.error('At least one team (Home or Away) must be assigned to create a game');
        return;
      }

      console.log('🎮 Modal handleSubmit - formData:', formData);
      console.log('🎮 Modal handleSubmit - userId:', userId);

      const gameData = {
        game_name: formData.game_name.trim(),
        game_date: formData.game_date,
        game_time: formData.game_time,
        game_timezone: formData.game_timezone,
        sport_id: formData.sport_id,
        venue_id: formData.venue_id || null,
        venue_name: formData.venue_name || 'TBD',
        venue_address: formData.venue_address || 'TBD',
        field_location: formData.field_location?.trim() || 'TBD',
        game_description: formData.game_description?.trim() || '',
        minimum_players_required: formData.minimum_players_required || 5,
        age_group: formData.age_group,
        
        // Team configurations
        home_team_id: formData.home_team_id || null,
        away_team_id: formData.away_team_id || null,
        home_team_label: formData.home_team_label || 'Home',
        away_team_label: formData.away_team_label || 'Away',
        
        // Custom logos only if teams not selected AND not TBD
        home_team_logo: (!formData.home_team_id && !formData.home_team_is_tbd) ? teamLogos.home : undefined,
        away_team_logo: (!formData.away_team_id && !formData.away_team_is_tbd) ? teamLogos.away : undefined,
      };

      console.log('🎮 Modal handleSubmit - final gameData:', gameData);

      const newGame = await createGame(gameData);
      
      // Verify the game was actually created
      if (!newGame || !newGame.id) {
        throw new Error('Game creation failed - no game ID returned');
      }

      console.log('🎮 Game creation confirmed with ID:', newGame.id);
      toast.success(`Game "${newGame.game_name}" created successfully!`);
      setOpen(false);
      
      // Navigate to unified form using React Router (instant navigation)
      navigate(`/games/${newGame.id}/unified-form?type=regular`);
      
      // Reset form
      setCurrentStep(1);
      setSelectedDivision('');
      setFormData({
        game_name: '',
        game_date: '',
        game_time: '',
        game_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        venue_id: undefined,
        venue_name: '',
        venue_address: '',
        field_location: '',
        game_description: '',
        sport_id: undefined,
        age_group: undefined,
        minimum_players_required: 2, // Default fallback, will be overridden by sport config
        home_team_id: undefined,
        away_team_id: undefined,
        home_team_label: 'Home',
        away_team_label: 'Away',
        home_team_is_tbd: false,
        away_team_is_tbd: false
      });
      setTeamLogos({ home: null, away: null });
      
      onGameCreated?.();
    } catch (error) {
      console.error('🎮 Modal handleSubmit - Error creating game:', error);
      
      // Enhanced error handling with specific messages
      if (error instanceof Error) {
        if (error.message.includes('Authentication') || error.message.includes('auth')) {
          toast.error('Your session has expired. Please refresh the page and try again.', { 
            duration: 10000 
          });
        } else {
          toast.error(error.message, { duration: 8000 });
        }
      } else {
        toast.error('An unexpected error occurred. Please try again.', { duration: 8000 });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Game Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="game_name">Game Name *</Label>
                <Input
                  id="game_name"
                  value={formData.game_name}
                  onChange={(e) => handleInputChange('game_name', e.target.value)}
                  placeholder="e.g., Championship Finals, League Match"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-primary" />
                    Game Date *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          selectedDate 
                            ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90" 
                            : "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-background border shadow-md z-50" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateSelect}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label>Game Time *</Label>
                  <Input
                    type="time"
                    value={formData.game_time}
                    onChange={(e) => handleInputChange('game_time', e.target.value)}
                    required
                    className="w-full"
                    step="300"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="game_timezone">Timezone *</Label>
                <Select
                  value={formData.game_timezone}
                  onValueChange={(value) => handleInputChange('game_timezone', value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <SportVariantSelector
                selectedSportId={formData.sport_id}
                selectedVariantId={selectedSportVariant?.id}
                onSportChange={(sportId) => {
                  handleInputChange('sport_id', sportId);
                  // Reset variant when sport changes
                  setSelectedSportVariant(null);
                }}
                onVariantChange={(variant) => {
                  setSelectedSportVariant(variant);
                  if (variant) {
                    // Automatically set minimum players based on sport configuration
                    handleInputChange('minimum_players_required', variant.min_players_per_team);
                    handleInputChange('sport_variant_id', variant.id);
                    console.log(`Sport variant selected: ${variant.variant_name}, min players: ${variant.min_players_per_team}`);
                  }
                }}
              />

              <div>
                <Label htmlFor="age_group">Age Group *</Label>
                <Select 
                  value={formData.age_group || ''} 
                  onValueChange={(value) => handleInputChange('age_group', value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select age group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="13-and-under">13 and Under</SelectItem>
                    <SelectItem value="14-17">14-17</SelectItem>
                    <SelectItem value="18-plus">18+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DivisionSelector
                selectedDivision={selectedDivision}
                onDivisionChange={setSelectedDivision}
              />
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <EnhancedVenueSelector
            onVenueSelect={(venueData) => {
              setFormData(prev => ({
                ...prev,
                venue_id: venueData.venue_id,
                venue_name: venueData.venue_name,
                venue_address: venueData.venue_address
              }));
            }}
          />
        );

      case 3:
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Team Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Team Side Selection - Only show for coaches/team-owners */}
                {!isVenue && (
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">Are you the Home Team or the Away Team for this game?</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <Button
                        type="button"
                        variant={formData.created_by_team_side === 'home' ? 'default' : 'outline'}
                        onClick={() => handleInputChange('created_by_team_side', 'home')}
                        className="h-12 text-base"
                      >
                        <Home className="mr-2 h-5 w-5" />
                        Home Team
                      </Button>
                      <Button
                        type="button"
                        variant={formData.created_by_team_side === 'away' ? 'default' : 'outline'}
                        onClick={() => handleInputChange('created_by_team_side', 'away')}
                        className="h-12 text-base"
                      >
                        <Plane className="mr-2 h-5 w-5" />
                        Away Team
                      </Button>
                    </div>
                  </div>
                )}

                {/* Team Labels */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="home_team_label">Home Team Label</Label>
                    <Input
                      id="home_team_label"
                      value={formData.home_team_label}
                      onChange={(e) => handleInputChange('home_team_label', e.target.value)}
                      placeholder="Home Team"
                    />
                  </div>

                  <div>
                    <Label htmlFor="away_team_label">Away Team Label</Label>
                    <Input
                      id="away_team_label"
                      value={formData.away_team_label}
                      onChange={(e) => handleInputChange('away_team_label', e.target.value)}
                      placeholder="Away Team"
                    />
                  </div>
                </div>

                {/* Team Assignments */}
                {(isVenue || formData.created_by_team_side) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <Label>
                        {isVenue ? 'Home Team' : (formData.created_by_team_side === 'home' ? 'Your Team (Home)' : 'Opposing Team (Home)')}
                      </Label>
                      <Select 
                        value={formData.home_team_id ? formData.home_team_id.toString() : (formData.home_team_is_tbd ? 'tbd' : '')} 
                        onValueChange={(value) => {
                          if (value === 'tbd') {
                            setFormData(prev => ({ ...prev, home_team_id: undefined, home_team_is_tbd: true }));
                          } else {
                            setFormData(prev => ({ ...prev, home_team_id: parseInt(value), home_team_is_tbd: false }));
                          }
                        }}
                      >
                        <SelectTrigger className="bg-input border-border text-foreground">
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border text-popover-foreground z-[9999] max-h-60 overflow-y-auto">
                          {(isVenue || formData.created_by_team_side === 'away') && (
                            <SelectItem value="tbd" className="text-foreground hover:bg-accent focus:bg-accent">TBD (To Be Determined)</SelectItem>
                          )}
                          {homeTeamOptions.length === 0 ? (
                            <SelectItem value="no-home-teams" disabled className="text-muted-foreground">
                              No {selectedSportName || 'matching'} teams available
                            </SelectItem>
                          ) : (
                            homeTeamOptions.map((team) => (
                              <SelectItem 
                                key={team.team_id} 
                                value={team.team_id.toString()}
                                className="text-foreground hover:bg-accent focus:bg-accent"
                              >
                                {team.team_name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>

                      {/* Custom Logo Upload for Home Team */}
                      {!formData.home_team_id && !formData.home_team_is_tbd && (isVenue || formData.created_by_team_side === 'away') && (
                        <div className="bg-muted rounded-lg p-4">
                          <Label className="text-sm font-medium mb-2 block">Custom Logo (Optional)</Label>
                          <TeamLogoUpload
                            onLogoUpload={(url) => handleLogoUpload('home', url)}
                            currentLogo={teamLogos.home}
                            teamName={formData.home_team_label}
                          />
                        </div>
                      )}
                      {formData.home_team_is_tbd && (
                        <div className="bg-muted/50 rounded-lg p-4 text-center">
                          <p className="text-sm text-muted-foreground">Team will be determined later</p>
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      <Label>
                        {isVenue ? 'Away Team' : (formData.created_by_team_side === 'away' ? 'Your Team (Away)' : 'Opposing Team (Away)')}
                      </Label>
                      <Select 
                        value={formData.away_team_id ? formData.away_team_id.toString() : (formData.away_team_is_tbd ? 'tbd' : '')} 
                        onValueChange={(value) => {
                          if (value === 'tbd') {
                            setFormData(prev => ({ ...prev, away_team_id: undefined, away_team_is_tbd: true }));
                          } else {
                            setFormData(prev => ({ ...prev, away_team_id: parseInt(value), away_team_is_tbd: false }));
                          }
                        }}
                      >
                        <SelectTrigger className="bg-input border-border text-foreground">
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border text-popover-foreground z-[9999] max-h-60 overflow-y-auto">
                          {(isVenue || formData.created_by_team_side === 'home') && (
                            <SelectItem value="tbd" className="text-foreground hover:bg-accent focus:bg-accent">TBD (To Be Determined)</SelectItem>
                          )}
                          {awayTeamOptions.length === 0 ? (
                            <SelectItem value="no-away-teams" disabled className="text-muted-foreground">
                              No {selectedSportName || 'matching'} teams available
                            </SelectItem>
                          ) : (
                            awayTeamOptions.map((team) => (
                              <SelectItem 
                                key={team.team_id} 
                                value={team.team_id.toString()}
                                className="text-foreground hover:bg-accent focus:bg-accent"
                              >
                                {team.team_name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>

                      {/* Custom Logo Upload for Away Team */}
                      {!formData.away_team_id && !formData.away_team_is_tbd && (isVenue || formData.created_by_team_side === 'home') && (
                        <div className="bg-muted rounded-lg p-4">
                          <Label className="text-sm font-medium mb-2 block">Custom Logo (Optional)</Label>
                          <TeamLogoUpload
                            onLogoUpload={(url) => handleLogoUpload('away', url)}
                            currentLogo={teamLogos.away}
                            teamName={formData.away_team_label}
                          />
                        </div>
                      )}
                      {formData.away_team_is_tbd && (
                        <div className="bg-muted/50 rounded-lg p-4 text-center">
                          <p className="text-sm text-muted-foreground">Team will be determined later</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Game Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Game Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TeamGameCard
                    teamName={formData.home_team_id ? 
                      teams.find(t => t.team_id === formData.home_team_id)?.team_name : 
                      formData.home_team_is_tbd ? 'TBD (To Be Determined)' : formData.home_team_label
                    }
                    teamLogo={formData.home_team_id ? 
                      teams.find(t => t.team_id === formData.home_team_id)?.team_logo : 
                      (!formData.home_team_is_tbd ? teamLogos.home : undefined)
                    }
                    teamLabel={formData.home_team_label}
                    isConfirmed={false}
                    participantCount={0}
                    minimumPlayers={formData.minimum_players_required}
                    side="home"
                    className="opacity-75"
                  />
                  
                  <TeamGameCard
                    teamName={formData.away_team_id ? 
                      teams.find(t => t.team_id === formData.away_team_id)?.team_name : 
                      formData.away_team_is_tbd ? 'TBD (To Be Determined)' : formData.away_team_label
                    }
                    teamLogo={formData.away_team_id ? 
                      teams.find(t => t.team_id === formData.away_team_id)?.team_logo : 
                      (!formData.away_team_is_tbd ? teamLogos.away : undefined)
                    }
                    teamLabel={formData.away_team_label}
                    isConfirmed={false}
                    participantCount={0}
                    minimumPlayers={formData.minimum_players_required}
                    side="away"
                    className="opacity-75"
                  />
                </div>

                {/* Countdown Preview */}
                {formData.game_date && formData.game_time && (
                  <div className="mt-4">
                    <GameCountdownTimer
                      gameId="preview"
                      gameDate={formData.game_date}
                      gameTime={formData.game_time}
                      gameTimezone={formData.game_timezone}
                      gameStatus="pending"
                      variant="compact"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  const stepTitles = [
    'Game Details',
    'Venue Setup',
    'Team Configuration'
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Create New Game - {stepTitles[currentStep - 1]}
          </DialogTitle>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center justify-between mb-6">
          {stepTitles.map((title, index) => (
            <div
              key={index}
              className={cn(
                "flex items-center gap-2 text-sm",
                currentStep > index + 1 && "text-primary",
                currentStep === index + 1 && "text-foreground font-medium",
                currentStep < index + 1 && "text-muted-foreground"
              )}
            >
              <div className={cn(
                "w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs",
                currentStep > index + 1 && "bg-primary border-primary text-primary-foreground",
                currentStep === index + 1 && "border-primary text-primary",
                currentStep < index + 1 && "border-muted-foreground"
              )}>
                {index + 1}
              </div>
              <span className="hidden sm:inline">{title}</span>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="mb-6">
          {renderStepContent()}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between">
          <div>
            {currentStep > 1 && (
              <Button type="button" variant="outline" onClick={prevStep}>
                Previous
              </Button>
            )}
          </div>
          
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            
            {currentStep < stepTitles.length ? (
              <Button onClick={nextStep} disabled={!validateStep(currentStep)}>
                Next
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading}>
                {loading ? 'Creating...' : 'Create Game'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};